import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Bookmark, ExternalLink, Heart, Mic, SquarePlay } from 'lucide-react'
import { useSnackbar } from '@shared/ui/notification-context'
import { useTranslation } from 'react-i18next'
import legacyShortVideoContract from '@legacy-short-videos-contract'
import { useDashboardLearnerContext } from '@features/dashboard'
import { useShortVideosState } from '@features/short-videos/state/useShortVideosState'
import {
  getCurrentVideoBookmark,
  getCurrentVideoSubscribe,
  getShortVideoLikeCount,
  hasUserLikedShortVideo,
} from '@features/short-videos/shared/short-video-selectors'
import {
  getRelatedShortVideosByTopic,
  likeShortVideo,
  toggleShortVideoBookmark,
  toggleShortVideoSubscribe,
  type ShortVideo,
} from '@services/firebase/short-videos'
import {
  getShortVideoTranslationText,
  getUniqueShortVideosById,
  normalizeShortVideoLocale,
} from '@features/short-videos/shared/short-video-helpers'
import {
  LmsButton,
} from '@shared/ui'
import { ShortVideoChannelCard } from '@features/short-videos/catalog/ShortVideoChannelCard'
import { ShortVideoCardCollection } from '@features/short-videos/shared/ShortVideoCardCollection'
import { ShortVideoPracticeDialog } from './ShortVideoPracticeDialog'
import { ShortVideosStatusBlock } from '@features/short-videos/shared/ShortVideosStatusBlock'
import {
  ShortVideoLearningPlayer,
  type ShortVideoLearningPlayerHandle,
} from './ShortVideoLearningPlayer'
import { getYoutubeWatchUrl } from '@shared/media/youtube/youtube-embed-url'

const legacyShortVideoDetailCssVariables = {
  '--short-video-control-default': legacyShortVideoContract.legacyShortVideoColors.controlDefault,
  '--short-video-heart-active': legacyShortVideoContract.legacyShortVideoColors.heartActive,
  '--short-video-primary': legacyShortVideoContract.legacyShortVideoColors.primary,
  '--short-video-primary-darker': legacyShortVideoContract.legacyShortVideoColors.primaryDarker,
  '--short-video-red': legacyShortVideoContract.legacyShortVideoColors.red,
  '--short-video-secondary': legacyShortVideoContract.legacyShortVideoColors.secondary,
  '--short-video-white': legacyShortVideoContract.legacyShortVideoColors.white,
} as CSSProperties

export function ShortVideoDetailScreen() {
  const { enqueueSnackbar } = useSnackbar()
  const { i18n } = useTranslation('translation')
  const { state: dashboardState } = useDashboardLearnerContext()
  const { actions, state } = useShortVideosState()
  const [isPracticeOpen, setIsPracticeOpen] = useState(false)
  const playerRef = useRef<ShortVideoLearningPlayerHandle>(null)
  const [hasRelatedLoaded, setHasRelatedLoaded] = useState(false)
  const [isRelatedLoading, setIsRelatedLoading] = useState(false)
  const [relatedErrorMessage, setRelatedErrorMessage] = useState<string | null>(null)
  const [relatedVideos, setRelatedVideos] = useState<ShortVideo[]>([])
  const locale = normalizeShortVideoLocale(i18n.language)
  const video = state.selectedVideo
  const selectedVideoId = video?.id ?? null
  const topicChannels = useMemo(() => {
    if (!video) {
      return []
    }

    const channelIds = legacyShortVideoContract.getLegacyTopicChannelIds({
      video,
      videos: state.videos,
    })

    return channelIds
      .map((channelId) => state.channels.find((channel) => channel.id === channelId))
      .filter((channel): channel is NonNullable<typeof channel> => Boolean(channel))
  }, [state.channels, state.videos, video])

  const recommendedVideos = useMemo(() => {
    if (!video) {
      return []
    }

    const videosById = new Map(state.videos.map((item) => [item.id, item]))

    return getUniqueShortVideosById(
      legacyShortVideoContract
        .getLegacyRecommendedVideoIds({ video, videos: state.videos })
        .map((videoId) => videosById.get(videoId))
        .filter((item): item is ShortVideo => Boolean(item)),
    )
  }, [state.videos, video])

  useEffect(() => {
    if (!selectedVideoId) {
      return undefined
    }

    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ behavior: 'smooth', top: 0 })
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [selectedVideoId])

  useEffect(() => {
    if (!video) {
      return
    }

    let isMounted = true

    const loadRelatedVideos = async () => {
      try {
        setHasRelatedLoaded(false)
        setIsRelatedLoading(true)
        setRelatedErrorMessage(null)
        const nextRelated = await getRelatedShortVideosByTopic({
          excludeVideoId: video.id,
          topicId: video.topicId,
        })

        if (isMounted) {
          setRelatedVideos(getUniqueShortVideosById(nextRelated))
          setHasRelatedLoaded(true)
        }
      } catch (error) {
        if (isMounted) {
          const message =
            error instanceof Error
              ? error.message
              : 'Related short videos failed to load. Please try again.'

          setRelatedErrorMessage(message)
          setHasRelatedLoaded(true)
          enqueueSnackbar(message, { variant: 'error' })
        }
      } finally {
        if (isMounted) {
          setIsRelatedLoading(false)
        }
      }
    }

    void loadRelatedVideos()

    return () => {
      isMounted = false
    }
  }, [enqueueSnackbar, video])

  if (!video) {
    return null
  }

  const currentUserId = dashboardState.myUser?.id
  const currentBookmark = getCurrentVideoBookmark({
    bookmarks: state.bookmarks,
    userId: currentUserId,
    videoId: video.id,
  })
  const currentSubscribe = getCurrentVideoSubscribe({
    channelId: video.channelId,
    subscribes: state.subscribes,
    userId: currentUserId,
  })
  const hasLiked = hasUserLikedShortVideo({
    likes: state.likes,
    userId: currentUserId,
    videoId: video.id,
  })
  const likeCount = getShortVideoLikeCount({
    likes: state.likes,
    videoId: video.id,
  })
  const translation = getShortVideoTranslationText(video.translates, locale)
  const currentChannel = state.channels.find((channel) => channel.id === video.channelId) ?? null
  const currentTopic = state.topics.find((topic) => topic.id === video.topicId) ?? null
  const youtubeWatchUrl = video.youtubeId ? getYoutubeWatchUrl(video.youtubeId) : null

  const handleToggleBookmark = async () => {
    if (!dashboardState.myUser) {
      return
    }

    try {
      const result = await toggleShortVideoBookmark({
        currentBookmark,
        profile: dashboardState.myUser,
        videoId: video.id,
      })

      actions.setBookmarks(
        result
          ? [...state.bookmarks.filter((bookmark) => bookmark.id !== result.id), result]
          : state.bookmarks.filter((bookmark) => bookmark.id !== currentBookmark?.id),
      )
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Bookmark failed.', {
        variant: 'error',
      })
    }
  }

  const handleToggleSubscribe = async (channelId: string) => {
    if (!dashboardState.myUser) {
      return
    }

    const subscribe = getCurrentVideoSubscribe({
      channelId,
      subscribes: state.subscribes,
      userId: currentUserId,
    })

    try {
      const result = await toggleShortVideoSubscribe({
        channelId,
        currentSubscribe: subscribe,
        profile: dashboardState.myUser,
      })

      actions.setSubscribes(
        result
          ? [...state.subscribes.filter((item) => item.id !== result.id), result]
          : state.subscribes.filter((item) => item.id !== subscribe?.id),
      )
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Subscribe failed.', {
        variant: 'error',
      })
    }
  }

  const handleLikeVideo = async () => {
    if (!dashboardState.myUser || hasLiked) {
      return
    }

    try {
      await likeShortVideo({
        profile: dashboardState.myUser,
        videoId: video.id,
      })

      enqueueSnackbar('Liked', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Like failed.', {
        variant: 'error',
      })
    }
  }

  const handleSelectVideo = (nextVideo: ShortVideo) => {
    if (nextVideo.id === video.id) {
      return
    }

    setIsPracticeOpen(false)
    actions.setSelectedVideo(nextVideo)
  }

  return (
    <div
      className="shortVideoDetailSurface"
      style={legacyShortVideoDetailCssVariables}
    >
      <ShortVideoLearningPlayer
        key={video.id}
        locale={locale}
        onReturn={() => {
          actions.setSelectedVideo(null)
        }}
        ref={playerRef}
        video={video}
      />

      <section className="shortVideoDetailIntro">
        <span className="shortVideoDetailEyebrow">
          Main sentence
        </span>
        <h1 className="shortVideoDetailTitle">
          {video.title ?? 'Short video sentence'}
        </h1>
        {translation ? (
          <p className="shortVideoDetailTranslation">
            {translation}
          </p>
        ) : null}
        {currentChannel ? (
          <span className="shortVideoDetailChannelBadge">
            {currentChannel.title ?? 'Channel'}
          </span>
        ) : null}
      </section>

      <div className="shortVideoDetailControls">
        <LmsButton
          aria-label={`Like (${likeCount})`}
          aria-pressed={hasLiked}
          className={`shortVideoDetailActionButton shortVideoDetailControlLike${hasLiked ? ' isActive' : ''}`}
          disabled={hasLiked}
          onClick={() => void handleLikeVideo()}
          size="icon"
          variant="ghost"
        >
          <Heart aria-hidden="true" />
          {likeCount > 0 ? <span className="shortVideoDetailControlBadge">{likeCount}</span> : null}
        </LmsButton>
        <LmsButton
          aria-label={currentBookmark ? 'Remove bookmark' : 'Bookmark'}
          aria-pressed={Boolean(currentBookmark)}
          className={`shortVideoDetailActionButton shortVideoDetailControlBookmark${currentBookmark ? ' isActive' : ''}`}
          onClick={() => void handleToggleBookmark()}
          size="icon"
          variant="ghost"
        >
          <Bookmark aria-hidden="true" />
        </LmsButton>
        <LmsButton
          aria-label="Practice speaking"
          className="shortVideoDetailActionButton shortVideoDetailControlSpeak"
          onClick={() => {
            playerRef.current?.pause()
            setIsPracticeOpen(true)
          }}
          size="icon"
          variant="ghost"
        >
          <Mic aria-hidden="true" />
        </LmsButton>
      </div>

      <div className="shortVideoDetailPlayerActions">
        {youtubeWatchUrl ? (
          <LmsButton
            asChild
            leadingIcon={<SquarePlay aria-hidden="true" />}
            mobileFullWidth
            trailingIcon={<ExternalLink aria-hidden="true" />}
            variant="outline"
          >
            <a href={youtubeWatchUrl} rel="noreferrer" target="_blank">
              Open on YouTube
            </a>
          </LmsButton>
        ) : null}
        {currentChannel ? (
          <LmsButton
            aria-pressed={Boolean(currentSubscribe)}
            mobileFullWidth
            onClick={() => void handleToggleSubscribe(currentChannel.id)}
            variant={currentSubscribe ? 'secondary' : 'outline'}
          >
            {currentSubscribe ? 'Unsubscribe channel' : 'Subscribe channel'}
          </LmsButton>
        ) : null}
      </div>

      <section className="shortVideoDetailRelatedSection">
        <div
          className="shortVideoDetailSectionHeading"
        >
          <h2 className="shortVideosSectionTitle">
            {currentTopic?.title ?? '...'}
          </h2>
          <span className="shortVideosSectionMeta">
            Studying
          </span>
        </div>
        {isRelatedLoading || !hasRelatedLoaded ? (
          <ShortVideosStatusBlock
            body="Keep this area open while we find more videos from this topic."
            minHeight={220}
            title="Preparing related short videos"
            isLoading
          />
        ) : relatedErrorMessage ? (
          <ShortVideosStatusBlock
            body={relatedErrorMessage}
            minHeight={220}
            title="We could not load related short videos"
            tone="error"
          />
        ) : relatedVideos.length === 0 ? (
          <ShortVideosStatusBlock
            body="This topic loaded successfully, but no other published short videos are available yet."
            minHeight={220}
            title="No related short videos yet"
            tone="empty"
          />
        ) : (
          <ShortVideoCardCollection
            cellClassName="shortVideoDetailVideoCell"
            channels={state.channels}
            currentVideoId={video.id}
            gridClassName="shortVideoDetailVideoGrid"
            locale={locale}
            onSelect={handleSelectVideo}
            videos={getUniqueShortVideosById([video, ...relatedVideos])}
          />
        )}
      </section>

      {topicChannels.length > 0 ? (
        <section className="shortVideoDetailTopicChannelsSection">
          <h2 className="shortVideosSectionTitle">
            Channels with this topic
          </h2>
          <div className="shortVideoDetailTopicChannelsRail">
            {topicChannels.map((channel) => (
              <ShortVideoChannelCard
                channel={channel}
                compact
                key={channel.id}
                onToggleSubscribe={() => void handleToggleSubscribe(channel.id)}
                subscribeCount={
                  state.subscribes.filter((subscribe) => subscribe.channelId === channel.id)
                    .length
                }
                subscribed={getCurrentVideoSubscribe({
                  channelId: channel.id,
                  subscribes: state.subscribes,
                  userId: currentUserId,
                })}
              />
            ))}
          </div>
        </section>
      ) : null}

      {recommendedVideos.length > 0 ? (
        <section className="shortVideoDetailRecommendedSection">
          <h2 className="shortVideosSectionTitle">
            Recommended video
          </h2>
          <ShortVideoCardCollection
            cellClassName="shortVideoDetailVideoCell"
            channels={state.channels}
            gridClassName="shortVideoDetailVideoGrid"
            locale={locale}
            onSelect={handleSelectVideo}
            videos={recommendedVideos}
          />
        </section>
      ) : null}

      {isPracticeOpen ? (
        <ShortVideoPracticeDialog
          key={`practice-${video.id}`}
          locale={locale}
          onClose={() => {
            setIsPracticeOpen(false)
          }}
          open
          video={video}
        />
      ) : null}
    </div>
  )
}
